<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import api from '@/api/client'
import ArmyTokenPreview from '@/components/army/ArmyTokenPreview.vue'
import { roleColor, type SiteRole } from '@/services/rolePresentation'
import { useAuthStore } from '@/stores/auth'

interface FanArmy { id: string; name: string; name_pl?: string; name_eng?: string }
interface PublicProfile {
  id: string
  username: string
  role: SiteRole
  logo_url: string
  logo_rect?: { x?: number; y?: number; w?: number; h?: number } | null
  logo_kind?: 'h' | 'b' | 'a' | 'u' | 'f' | 'l' | 'm'
  friend_count: number
  army_count: number
  fan_armies?: FanArmy[]
  is_friend: boolean
  friend_request_pending?: boolean
  nickname_censored?: boolean
  muted?: boolean
  banned?: boolean
  member_since: string
}

const props = defineProps<{ visible: boolean; username: string; userId?: string }>()
const emit = defineEmits<{ close: [] }>()
const router = useRouter()
const auth = useAuthStore()
const profile = ref<PublicProfile | null>(null)
const loading = ref(false)
const error = ref('')
const armiesExpanded = ref(false)
const relationBusy = ref(false)

const relationLabel = computed(() => {
  if (profile.value?.is_friend) return 'Friend'
  if (profile.value?.friend_request_pending) return 'Invited to friends'
  return 'Add a friend'
})
const moderationSuffix = computed(() => {
  if (!auth.isStaff) return ''
  return `${profile.value?.nickname_censored ? ' ⚠️' : ''}${profile.value?.banned ? ' ⛔' : ''}${profile.value?.muted ? ' 🤐' : ''}`
})

async function load(): Promise<void> {
  const identifier = props.userId || props.username
  if (!props.visible || !identifier) return
  loading.value = true
  error.value = ''
  profile.value = null
  try {
    const { data } = await api.get<PublicProfile>(`/social/users/${encodeURIComponent(identifier)}/profile`)
    profile.value = data
    armiesExpanded.value = false
  } catch (requestError: any) {
    error.value = requestError?.response?.data?.error || 'Profile could not be loaded.'
  } finally {
    loading.value = false
  }
}

async function addFriend(): Promise<void> {
  if (!profile.value || profile.value.is_friend || profile.value.friend_request_pending || relationBusy.value) return
  relationBusy.value = true
  try {
    const { data } = await api.post<{ accepted?: boolean }>('/social/friends/request', { user_id: profile.value.id })
    profile.value.friend_request_pending = !data.accepted
    profile.value.is_friend = data.accepted === true
  } catch (requestError: any) {
    error.value = requestError?.response?.data?.error || 'Friend request could not be sent.'
  } finally {
    relationBusy.value = false
  }
}

function openArmy(army: FanArmy): void {
  const resolved = router.resolve({ name: 'army-detail', params: { id: army.id } })
  window.open(resolved.href, '_blank', 'noopener,noreferrer')
}

watch(() => [props.visible, props.username, props.userId], load, { immediate: true })
</script>

<template>
  <Teleport to="body">
    <div v-if="visible" class="player-profile-overlay" @pointerdown.self="emit('close')">
      <section class="player-profile-card nhex-popup-surface nhex-popup--player-profile" @pointerdown.stop>
        <img class="profile-art" src="/media/site/Posterunek.avif" alt="" draggable="false" />
        <button class="profile-close" type="button" aria-label="Close" @click="emit('close')">&times;</button>
        <p v-if="loading" class="profile-state">Loading profile...</p>
        <p v-else-if="error" class="profile-state error">{{ error }}</p>
        <template v-else-if="profile">
          <div class="profile-identity">
            <div class="profile-logo">
              <ArmyTokenPreview
                :path="profile.logo_url || '/media/site/icon.avif'"
                :kind="profile.logo_kind || 'u'"
                :rect="profile.logo_rect"
              />
            </div>
            <div>
              <h2 :style="{ color: roleColor(profile.role) }">{{ profile.username }}{{ moderationSuffix }}</h2>
              <span class="role-label">{{ profile.role }}</span>
            </div>
          </div>
          <dl class="profile-stats">
            <div><dt>Friends</dt><dd>{{ profile.friend_count }}</dd></div>
            <button class="profile-stat-action" type="button" :disabled="!profile.army_count" @click="armiesExpanded = !armiesExpanded">
              <span class="profile-stat-label">Fan armies</span><span class="profile-stat-value">{{ profile.army_count }}</span>
            </button>
            <button class="profile-stat-action" type="button" :disabled="profile.is_friend || profile.friend_request_pending || relationBusy" @click="addFriend">
              <span class="profile-stat-label">Relation</span><span class="profile-stat-value">{{ relationLabel }}</span>
            </button>
            <div><dt>Member since</dt><dd>{{ new Date(profile.member_since).toLocaleDateString() }}</dd></div>
          </dl>
          <div v-if="armiesExpanded && profile.fan_armies?.length" class="profile-armies">
            <button v-for="army in profile.fan_armies" :key="army.id" type="button" @click="openArmy(army)">
              {{ army.name }}
            </button>
          </div>
        </template>
      </section>
    </div>
  </Teleport>
</template>

<style scoped>
.player-profile-overlay { position: fixed; inset: 0; z-index: 12000; padding: 16px; display: grid; place-items: center; background: rgba(0,0,0,.7); }
.player-profile-card { position: relative; isolation: isolate; width: min(470px,100%); min-height: 250px; padding: 24px; overflow: hidden; border: 1px solid rgba(255,255,255,.16); border-radius: 8px; background: #090c10; box-shadow: 0 24px 70px rgba(0,0,0,.66); color: #d8dee4; }
.profile-art { position: absolute; z-index: -1; right: -10%; bottom: 0; width: 68%; max-height: 92%; object-fit: contain; opacity: .2; pointer-events: none; }
.profile-close { position: absolute; top: 7px; right: 9px; width: 31px; height: 31px; border: 0; background: transparent; color: #b6bec6; font-size: 25px; cursor: pointer; }
.profile-identity { display: flex; align-items: center; gap: 15px; margin: 6px 35px 24px 0; }
.profile-logo { width: 82px; height: 74px; flex: 0 0 auto; display: grid; place-items: center; overflow: hidden; }
.profile-logo :deep(.token-svg) { width: 100%; height: 100%; }
.profile-identity h2 { max-width: 280px; margin: 0; overflow: hidden; text-overflow: ellipsis; font: 700 23px Inconsolata, monospace; }
.role-label { color: #8c97a1; font: 700 11px Inconsolata, monospace; text-transform: uppercase; }
.profile-stats { display: grid; grid-template-columns: 1fr 1fr; gap: 7px; margin: 0; }
.profile-stats > div, .profile-stat-action { min-width: 0; padding: 9px 10px; border: 1px solid rgba(255,255,255,.09); border-radius: 5px; background: rgba(12,17,22,.82); text-align: left; }
.profile-stat-action { color: inherit; cursor: pointer; }
.profile-stat-action:not(:disabled):hover { border-color: #489cd6; background: rgba(20,36,49,.9); }
.profile-stat-action:disabled { cursor: default; }
.profile-stats dt, .profile-stat-label { display: block; color: #77838e; font: 11px Inconsolata, monospace; }
.profile-stats dd, .profile-stat-value { display: block; margin: 2px 0 0; color: #e5e9ed; font: 700 13px Inconsolata, monospace; }
.profile-state { margin: 90px 0; color: #89949f; text-align: center; font: 13px Inconsolata, monospace; }
.profile-state.error { color: #e17777; }
.profile-armies { grid-column: 1 / -1; display: grid; gap: 5px; max-height: 130px; margin-top: 10px; overflow: auto; }
.profile-armies button { min-height: 34px; padding: 0 10px; border: 1px solid rgba(255,255,255,.1); border-radius: 4px; background: rgba(12,17,22,.9); color: #dce2e8; text-align: left; font: 700 12px Inconsolata, monospace; cursor: pointer; }
.profile-armies button:hover { border-color: #489cd6; color: #fff; }
</style>
